/**
 * fg-app-bridges.js
 *
 * Glue code between the theme and third-party apps (Boost, HulkApps Wishlist, Shopper Approved, GTM).
 * Moved out of layout/theme.liquid so it is cached by the browser instead of being re-sent inline
 * with every page. Loaded with `defer` from the layout, so it runs once the document is parsed
 * and before DOMContentLoaded.
 */

/* =====================================================================
 * Boost "Frequently bought together" add-to-cart, GA4 select_item,
 * Shopper Approved loader, and collection pagination (H1 / intro / scroll)
 * ===================================================================== */
(function () {
  // The cart drawer lives in the header section. Its id differs per theme instance
  // (e.g. after duplicating or republishing the theme), so look it up instead of hardcoding it.
  function getCartSectionId() {
    var el = document.querySelector('.shopify-section[id$="__header_section"]');
    return el ? el.id.replace(/^shopify-section-/, '') : 'sections--26767693021227__header_section';
  }
  var CART_SECTION_ID = getCartSectionId();

  // Locale-safe root (e.g. '/en-us/' on translated stores, '/' otherwise)
  var SHOP_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  function saLoadScript(src) {
    var js = window.document.createElement('script');
    js.src = src;
    js.type = 'text/javascript';
    document.getElementsByTagName('head')[0].appendChild(js);
  }

  function delay(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  /* ---------------------------------------------------------------
  * 1. Variant ID cache + prefetch (locale-safe handle extraction)
  * --------------------------------------------------------------- */
  var variantIdCache = {}; // handle -> Promise<variantId|null>

  function extractHandle(link) {
    var href = link && link.getAttribute('href');
    if (!href) return null;
    // Works with /products/handle, /en-us/products/handle, ?variant=..., etc.
    var m = href.match(/\/products\/([^\/?#]+)/);
    return m ? m[1] : null;
  }

  function getVariantIdByHandle(handle) {
    if (!variantIdCache[handle]) {
      variantIdCache[handle] = fetch(SHOP_ROOT + 'products/' + handle + '.json')
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data && data.product && data.product.variants && data.product.variants.length > 0) {
            return data.product.variants[0].id;
          }
          return null;
        })
        .catch(function () {
          delete variantIdCache[handle]; // allow retry next time
          return null;
        });
    }
    return variantIdCache[handle];
  }

  function prefetchFbtVariants() {
    var links = document.querySelectorAll(
      '.boost-sd__recommendation-bundle a.boost-sd__product-link'
    );
    links.forEach(function (link) {
      var handle = extractHandle(link);
      if (handle) getVariantIdByHandle(handle);
    });
  }

  /* ---------------------------------------------------------------
  * 2. Cart drawer update (uses section HTML bundled in add response)
  * --------------------------------------------------------------- */
  function applyCartSectionHtml(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');

    var newItems = doc.querySelector('.cart-drawer__content');
    var currentItems = document.querySelector('.cart-drawer__content');
    if (newItems && currentItems) currentItems.innerHTML = newItems.innerHTML;

    var newCount = doc.querySelector('.cart-bubble__text-count');
    var currentCount = document.querySelector('.cart-bubble__text-count');
    if (newCount && currentCount) currentCount.innerHTML = newCount.innerHTML;

    // Update cart total
    var newTotal = doc.querySelector('[data-cart-subtotal]');
    var currentTotal = document.querySelector('[data-cart-subtotal]');
    if (newTotal && currentTotal) {
      currentTotal.innerHTML = newTotal.innerHTML;
      currentTotal.setAttribute('value', newTotal.getAttribute('value'));
    }

    // Update the "design your class" header (item count + subtotal + installments)
    var newDesign = doc.querySelector('.design-your-class');
    var currentDesign = document.querySelector('.design-your-class');
    if (newDesign && currentDesign) currentDesign.innerHTML = newDesign.innerHTML;
  }

  // Re-render the cart drawer through the Section Rendering API (used when the add
  // response didn't include the section HTML, or after a partial failure).
  function refreshCartDrawer() {
    return fetch(SHOP_ROOT + '?sections=' + encodeURIComponent(CART_SECTION_ID))
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (data && data[CART_SECTION_ID]) applyCartSectionHtml(data[CART_SECTION_ID]);
      })
      .catch(function (err) {
        console.warn('[FBT] Could not refresh cart drawer:', err);
      });
  }

  /* ---------------------------------------------------------------
  * 3. Detect the theme's own /cart/add request — BOTH fetch and XHR.
  *    Lets us chain our add right behind the theme's instead of
  *    racing it (concurrent cart writes are what drop items).
  * --------------------------------------------------------------- */
  var themeCartAddPromise = null;
  var fbtAddInFlight = false;

  (function interceptFetch() {
    var origFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var result = origFetch.apply(this, arguments);
      if (!fbtAddInFlight && url.indexOf('/cart/add') !== -1) {
        themeCartAddPromise = result.catch(function () {});
      }
      return result;
    };
  })();

  (function interceptXHR() {
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._fbtCartAdd = String(url || '').indexOf('/cart/add') !== -1;
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      if (this._fbtCartAdd) {
        var xhr = this;
        themeCartAddPromise = new Promise(function (resolve) {
          xhr.addEventListener('loadend', resolve);
        });
      }
      return origSend.apply(this, arguments);
    };
  })();

  // Resolves once the theme's add has settled, or after a short ceiling.
  // IMPORTANT: consumes (nulls out) themeCartAddPromise once read, so a
  // stale promise from a previous click can never be reused on the next
  // click — that was silently defeating the whole point of this queue.
  function waitForThemeAdd() {
    var startedAt = Date.now();
    return new Promise(function (resolve) {
      var poll = setInterval(function () {
        if (themeCartAddPromise) {
          clearInterval(poll);
          var pending = themeCartAddPromise;
          themeCartAddPromise = null; // consume so future clicks start fresh
          Promise.race([pending, delay(1500)]).then(resolve);
        } else if (Date.now() - startedAt >= 1200) {
          clearInterval(poll);
          resolve(); // theme request never observed — proceed anyway
        }
      }, 50);
    });
  }

  /* ---------------------------------------------------------------
  * 4. Cart add with: keepalive (survives page navigation),
  *    retry on busy/transient errors, and per-item fallback when
  *    a batch 422s (so one bad item doesn't block the rest).
  * --------------------------------------------------------------- */
  function postCartAdd(items, attempt) {
    attempt = attempt || 0;
    fbtAddInFlight = true;
    return fetch(SHOP_ROOT + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // keepalive lets the request complete even if the theme
      // navigates/redirects right after its own add (empty-cart case)
      keepalive: true,
      body: JSON.stringify({ items: items, sections: CART_SECTION_ID })
    })
      .then(function (res) {
        fbtAddInFlight = false;
        // Retry once or twice on transient/conflict errors
        if ((res.status === 409 || res.status === 429 || res.status >= 500) && attempt < 2) {
          console.warn('[FBT] Cart busy (' + res.status + '), retrying...');
          return delay(400 * (attempt + 1)).then(function () {
            return postCartAdd(items, attempt + 1);
          });
        }
        return res.json().then(function (data) {
          return { ok: res.ok, status: res.status, data: data };
        });
      })
      .catch(function (err) {
        fbtAddInFlight = false;
        if (attempt < 2) {
          return delay(400 * (attempt + 1)).then(function () {
            return postCartAdd(items, attempt + 1);
          });
        }
        throw err;
      });
  }

  function addItemsResilient(items) {
    return postCartAdd(items).then(function (result) {
      if (result.ok) return result.data;

      console.warn('[FBT] Batch add failed (' + result.status + '):', result.data);

      // /cart/add.js is all-or-nothing: one invalid item fails the whole
      // batch. Fall back to adding items one by one, skipping failures.
      if (items.length > 1) {
        var lastGood = null;
        return items.reduce(function (chain, item) {
          return chain.then(function () {
            return postCartAdd([item]).then(function (r) {
              if (r.ok) lastGood = r.data;
              else console.warn('[FBT] Skipping item that failed to add:', item, r.data);
            });
          });
        }, Promise.resolve()).then(function () { return lastGood; });
      }

      throw result.data;
    });
  }

  /* ---------------------------------------------------------------
  * 5. Collect checked FBT items
  * --------------------------------------------------------------- */
  function collectCheckedVariantIds() {
    var checkedItems = document.querySelectorAll(
      '.boost-sd__recommendation-bundle-checkbox-box--checked'
    );

    var variantPromises = [];

    checkedItems.forEach(function (checkbox) {
      var productId = null;
      var idMatch = checkbox.id && checkbox.id.match(/(\d+)$/);
      if (idMatch) productId = idMatch[1];

      if (!productId) {
        console.warn('[FBT] Could not resolve productId for checkbox:', checkbox);
        return;
      }

      var dropdown = document.querySelector(
        '.boost-sd__dropdown[data-product-id="' + productId + '"]'
      );

      if (dropdown) {
        var selectedOption = dropdown.querySelector('.boost-sd__dropdown-option--selected');
        var variantId = selectedOption && selectedOption.getAttribute('data-variant-id');
        if (variantId) variantPromises.push(Promise.resolve(parseInt(variantId, 10)));
      } else {
        var imageItem = checkbox.closest('.boost-sd__recommendation-bundle-image-item');
        var productLink = imageItem && imageItem.querySelector('a.boost-sd__product-link');
        var handle = extractHandle(productLink);

        if (handle) {
          variantPromises.push(getVariantIdByHandle(handle));
        } else {
          console.warn('[FBT] Could not resolve handle for productId:', productId);
        }
      }
    });

    return Promise.all(variantPromises).then(function (ids) {
      return ids.filter(function (id) { return !!id; });
    });
  }

  /* ---------------------------------------------------------------
  * 6. ATC handling via EVENT DELEGATION on document.
  *    Survives Boost/theme re-rendering the button — no listener
  *    is ever "lost", and no polling/observer is needed for it.
  * --------------------------------------------------------------- */
  var fbtBusy = false;
  var FBT_HARD_TIMEOUT_MS = 10000;

  document.addEventListener('click', function (e) {
    var atcBtn = e.target.closest && e.target.closest('[data-testid="standalone-add-to-cart"]');
    if (!atcBtn) return;

    if (fbtBusy) {
      return;
    }

    // Capture checked state + start resolving variant IDs IMMEDIATELY,
    // before the theme has a chance to navigate or re-render anything.
    var variantIdsPromise = collectCheckedVariantIds();

    fbtBusy = true;

    // Hard ceiling so a fetch that never settles can't wedge fbtBusy
    // permanently and block all future FBT adds.
    var releasedByTimeout = false;
    var hardTimeout = setTimeout(function () {
      releasedByTimeout = true;
      fbtBusy = false;
      console.warn('[FBT] Hard timeout reached, releasing busy flag.');
    }, FBT_HARD_TIMEOUT_MS);

    Promise.all([variantIdsPromise, waitForThemeAdd()])
      .then(function (results) {
        var variantIds = results[0];
        if (!variantIds.length) {
          return null;
        }

        var items = variantIds.map(function (id) {
          return { id: id, quantity: 1 };
        });

        return addItemsResilient(items);
      })
      .then(function (data) {
        clearTimeout(hardTimeout);
        if (!releasedByTimeout) fbtBusy = false;
        if (!data) return;
        if (data.sections && data.sections[CART_SECTION_ID]) {
          applyCartSectionHtml(data.sections[CART_SECTION_ID]);
        } else {
          refreshCartDrawer();
        }
      })
      .catch(function (err) {
        clearTimeout(hardTimeout);
        if (!releasedByTimeout) fbtBusy = false;
        console.error('[FBT] Error adding FBT items:', err);
        // Drawer may be stale if some items added before the failure
        refreshCartDrawer();
      });
  });

  /* ---------------------------------------------------------------
  * 9. Product item click — GA4/GTM select_item event
  *    Delegated on document so it survives Boost re-rendering the
  *    grid (filters, sort, pagination via AJAX) without needing to
  *    re-bind listeners to new DOM nodes.
  *    Only the clicked product's own data is sent — never the full
  *    grid/list of products on the page.
  * --------------------------------------------------------------- */

  document.addEventListener('click', function (e) {
    var item = e.target.closest && e.target.closest('.boost-sd__product-item');
    if (!item) return;

    var productId = item.getAttribute('data-product-id');
    if (!productId) return;

    var productData = {};
    try {
      productData = JSON.parse(item.getAttribute('data-product')) || {};
    } catch (err) {}

    var titleEl = item.querySelector('.boost-sd__product-title');
    var title = titleEl ? titleEl.textContent.trim() : null;

    var product = {
      item_id: productId,
      item_name: title,
      price: productData.priceMin ? parseFloat(productData.priceMin) : null,
      discount: (productData.priceMin && productData.compareAtPriceMin)
        ? (parseFloat(productData.compareAtPriceMin) - parseFloat(productData.priceMin)).toFixed(2)
        : null
    };

    if (window.dataLayer) {
      // GA4 requires clearing the previous ecommerce object first
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'select_item',
        ecommerce: {
          items: [product]
        }
      });
    }
  });

  // Warm the variant cache the moment a user checks an FBT box,
  // in case the bundle rendered after the initial prefetch pass.
  document.addEventListener('click', function (e) {
    var box = e.target.closest && e.target.closest('.boost-sd__recommendation-bundle-checkbox-box');
    if (!box) return;
    var imageItem = box.closest('.boost-sd__recommendation-bundle-image-item');
    var link = imageItem && imageItem.querySelector('a.boost-sd__product-link');
    var handle = extractHandle(link);
    if (handle) getVariantIdByHandle(handle);
  });

  /* ---------------------------------------------------------------
  * 7. Init
  * --------------------------------------------------------------- */
  document.addEventListener('DOMContentLoaded', function () {
    // Hide Boost FBT's own Add to Cart button immediately
    var style = document.createElement('style');
    style.innerHTML =
      '.boost-sd__recommendation-bundle .boost-sd__btn-add-to-cart { display: none !important; }';
    document.head.appendChild(style);

    // Shopper Approved widget (deferred so it doesn't compete during load)
    setTimeout(function () {
      saLoadScript('https://www.shopperapproved.com/widgets/group2.0/21101.js');
    }, 2000);

    var wishlistInitialized = false;
    var fbtPrefetched = false;

    function onDomSettled() {
      if (!wishlistInitialized && document.querySelector('.boost-sd__product-item')) {
        if (window.hulkappsWishlistCollection &&
            typeof window.hulkappsWishlistCollection.init === 'function') {
          window.hulkappsWishlistCollection.init();
        }
        wishlistInitialized = true;
      }

      if (!fbtPrefetched &&
          document.querySelector('.boost-sd__recommendation-bundle a.boost-sd__product-link')) {
        prefetchFbtVariants();
        fbtPrefetched = true;
      }

      if (wishlistInitialized && fbtPrefetched && observer) {
        observer.disconnect();
        observer = null;
      }
    }

    // FIX 2: Debounce so a burst of mutations (Boost re-rendering a whole
    // grid) collapses into a single check per animation frame instead of
    // running two querySelector scans once per mutation record.
    var rafScheduled = false;
    function scheduleDomSettledCheck() {
      if (rafScheduled) return;
      rafScheduled = true;
      requestAnimationFrame(function () {
        rafScheduled = false;
        onDomSettled();
      });
    }

    // FIX 2: Watch the main content area rather than the whole document
    // body, so header/announcement-bar/footer changes don't trigger this.
    var observerTarget = document.getElementById('MainContent') || document.body;
    var observer = new MutationObserver(scheduleDomSettledCheck);
    observer.observe(observerTarget, { childList: true, subtree: true });
    onDomSettled();

    setTimeout(function () {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    }, 30000);
  });

  /* ---------------------------------------------------------------
  * 8. PLP paginated page updates (FULL-RELOAD pagination)
  *    Boost paginates via full server-side navigation on this store
  *    (the whole page reloads), so there is NO pushState/AJAX to
  *    intercept. We simply run on each fresh page load:
  *      - H1: appends "- Page N" on pages 2+, restores on page 1
  *      - Intro content: hidden on pages 2+, visible on page 1
  *        (only needed if the server doesn't already do this)
  *      - Scroll: on pages 2+, scrolls to the grid/filter area once
  *        the target exists. No scroll-suppression needed - the page
  *        loads at the top and we move it down a single time.
  * --------------------------------------------------------------- */
  (function initPlpPageUpdates() {
    if (window.location.pathname.indexOf('/collections/') === -1) return;

    var onReady = function (fn) {
      if (document.readyState !== 'loading') {
        fn();
      } else {
        document.addEventListener('DOMContentLoaded', fn, { once: true });
      }
    };

    function getPageNum() {
      return parseInt(new URL(window.location.href).searchParams.get('page') || '1', 10);
    }

    // H1: store the original text once, then append page suffix as needed
    function updateH1(pageNum) {
      var h1 = document.querySelector('h1');
      if (!h1) return;
      var baseTitle = h1.getAttribute('data-base-title');
      if (!baseTitle) {
        baseTitle = h1.textContent.trim();
        h1.setAttribute('data-base-title', baseTitle);
      }
      h1.textContent = pageNum > 1 ? baseTitle + ' \u2013 Page ' + pageNum : baseTitle;
    }

    // Intro content: the read-more block that wraps the collection description
    function updateIntro(pageNum) {
      var intro = document.querySelector('[data-read-more="true"]');
      if (!intro) return;
      intro.style.display = pageNum > 1 ? 'none' : '';
    }

    function applyPlpUpdates() {
      var pageNum = getPageNum();
      updateH1(pageNum);
      updateIntro(pageNum);
    }

    // Full-reload pagination: on page 2+, scroll to the grid once the
    // target exists. Breakpoint-aware; falls back to .boost-sd-container.
    function scrollToGridOnLoad() {
      if (getPageNum() <= 1) return; // page 1 stays at the top

      var selector = window.innerWidth < 750
        ? '.facets.facets--vertical.spacing-style'   // mobile
        : '.boost-sd-left.boost-filter-tree-column'; // desktop

      function tryScroll() {
        var target = document.querySelector(selector)
          || document.querySelector('.boost-sd-container');
        if (!target) return false;
        var top = target.getBoundingClientRect().top + window.pageYOffset - 150;
        window.scrollTo({ top: top, behavior: 'auto' });
        return true;
      }

      // Boost may render the grid after DOMContentLoaded, so retry until
      // the target appears (or give up after ~5s).
      if (tryScroll()) return;

      // FIX 3: Debounce so a burst of mutations only triggers one
      // tryScroll() check per animation frame instead of one per
      // mutation record (tryScroll does a forced layout read via
      // getBoundingClientRect(), so this matters more than usual).
      var rafScheduled = false;
      var observer = new MutationObserver(function () {
        if (rafScheduled) return;
        rafScheduled = true;
        requestAnimationFrame(function () {
          rafScheduled = false;
          if (tryScroll()) observer.disconnect();
        });
      });

      // FIX 3: Scope to the main content area rather than the whole
      // document body.
      var observerTarget = document.getElementById('MainContent') || document.body;
      observer.observe(observerTarget, { childList: true, subtree: true });
      setTimeout(function () { observer.disconnect(); }, 5000);
    }

    // Run on every (full) page load
    onReady(function () {
      applyPlpUpdates();    // H1 + intro
      scrollToGridOnLoad(); // scroll to grid on page 2+
    });
  })();
})();

/* =====================================================================
 * Move HulkApps wishlist buttons into Boost's no-redirect slot on product cards
 * ===================================================================== */
// Boost ships its own designated, empty wishlist slot on every card,
// marked data-no-redirect="true" — this is Boost's own mechanism for
// exempting an element from the card's own click-to-navigate handler
// (filter.module.js). HulkApps' wishlist button currently renders as
// a sibling OUTSIDE this slot (at the end of .boost-sd__product-item),
// which is why clicking it triggers Boost's navigation.
//
// Rather than fighting event propagation (Boost's navigate handler
// and HulkApps' own add-to-wishlist handler sit at different levels
// in the bubble chain, so no single stopPropagation() call can block
// one without also blocking the other), this moves the EXISTING
// button element into Boost's no-redirect slot. Moving (not cloning)
// preserves any listeners already bound to it, so HulkApps' own
// add-to-wishlist logic keeps working exactly as before — Boost's
// own exemption check does the rest.
(function () {
  var MOVE_FLAG = 'data-relocated-to-no-redirect';

  function relocateWishlistButtons(root) {
    var scope = root || document;
    var items = scope.querySelectorAll ? scope.querySelectorAll('.boost-sd__product-item') : [];
    items.forEach(function (item) {
      var slot = item.querySelector('.boost-product-wishlist[data-no-redirect]');
      if (!slot) return;

      var buttons = item.querySelectorAll(
        'a.wishlist-btn:not([' + MOVE_FLAG + ']), a.grid-wishlist-btn:not([' + MOVE_FLAG + '])'
      );
      // Duplicate buttons can appear across separate render passes
      // (e.g. HulkApps inserting a second one slightly after the
      // first), so check the slot's CURRENT contents each time
      // rather than relying on index within a single scan — that
      // was the bug: each pass only saw the one new unflagged
      // button and treated it as "the first" even when one was
      // already sitting in the slot from an earlier pass.
      buttons.forEach(function (btn) {
        btn.setAttribute(MOVE_FLAG, 'true');
        var slotAlreadyHasButton = slot.querySelector('a.wishlist-btn, a.grid-wishlist-btn');
        if (slotAlreadyHasButton) {
          btn.remove();
        } else {
          slot.appendChild(btn); // moves the existing node — listeners stay intact
        }
      });
    });
  }

  function init() {
    relocateWishlistButtons();

    // Boost re-renders the grid on filter/sort/search, recreating
    // both the no-redirect slots and the wishlist buttons, so this
    // keeps re-checking and re-relocating as new ones appear.
    var rafScheduled = false;
    var observerTarget = document.getElementById('MainContent') || document.body;
    var observer = new MutationObserver(function () {
      if (rafScheduled) return;
      rafScheduled = true;
      requestAnimationFrame(function () {
        rafScheduled = false;
        relocateWishlistButtons();
      });
    });
    observer.observe(observerTarget, { childList: true, subtree: true });
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  }
})();

/* =====================================================================
 * HulkApps wishlist: resolve missing variant ids before status checks
 * ===================================================================== */
(function () {
  var interceptInterval = setInterval(function () {
    if (window.HulkappWishlist && typeof window.HulkappWishlist._checkForProductStatus === 'function') {
      clearInterval(interceptInterval);

      var originalCheck = window.HulkappWishlist._checkForProductStatus;

      window.HulkappWishlist._checkForProductStatus = function (modules, flag) {
        if (window.hulkAppsRunningFix) {
          originalCheck.apply(this, arguments);
          return;
        }

        var buttons = Array.from(modules);
        var fetchPromises = [];

        buttons.forEach(function (btn) {
          var handle = btn.getAttribute('data-prohandle');
          var varId = btn.getAttribute('data-varid');

          if (!varId && handle) {
            var apiCall = fetch('/products/' + handle + '.js')
              .then(function (res) {
                return res.json();
              })
              .then(function (productData) {
                if (productData && productData.variants && productData.variants.length > 0) {
                  var fetchedId = productData.variants[0].id;
                  btn.setAttribute('data-varid', fetchedId);
                }
              })
              .catch(function (err) {
                console.error('HulkApps Interceptor: AJAX variant resolution failed', err);
              });

            fetchPromises.push(apiCall);
          }
        });

        Promise.all(fetchPromises).then(function () {
          window.hulkAppsRunningFix = true;
          originalCheck.call(window.HulkappWishlist, buttons, flag);
          window.hulkAppsRunningFix = false;

          if (!window.hulkAppsBackupCheckFired) {
            window.hulkAppsBackupCheckFired = true;

            setTimeout(function () {
              var backupButtons = Array.from(document.querySelectorAll('.wishlist-btn[data-proid], a[data-proid]'));

              if (backupButtons.length > 0) {
                window.hulkAppsRunningFix = true;
                originalCheck.call(window.HulkappWishlist, backupButtons, flag);
                window.hulkAppsRunningFix = false;
              }
            }, 3000);
          }
        });
      };

    }
  }, 100);

  setTimeout(function () {
    clearInterval(interceptInterval);
  }, 5000);
})();

/* =====================================================================
 * HulkApps wishlist -> GTM add_to_wishlist event
 * ===================================================================== */
(function() {

  // Using Event Capturing ('true') to bypass Boost app's stopPropagation()
  document.addEventListener('click', function(e) {
    var wishlistBtn = e.target.closest('.wishlist-btn, [data-prohandle], .heart-wishlist-animation');
    if (!wishlistBtn) return;

    // Extract handle: First check the button attribute. 
    // Fallback for PDP: Extract handle directly from the URL.
    var handle = wishlistBtn.getAttribute('data-prohandle');
    if (!handle && window.location.pathname.indexOf('/products/') !== -1) {
      handle = window.location.pathname.split('/products/')[1].split('/')[0];
    }

    if (!handle) return;

    // Prevent duplicate firing if user double-clicks
    if (wishlistBtn.getAttribute('data-gtm-tracked') === 'true') return;
    wishlistBtn.setAttribute('data-gtm-tracked', 'true');
    setTimeout(function() { wishlistBtn.removeAttribute('data-gtm-tracked'); }, 2000);


    fetch((window.Shopify?.routes?.root || '/') + 'products/' + handle + '.js')
      .then(function(response) { 
        if (!response.ok) throw new Error('Product not found or inaccessible');
        return response.json(); 
      })
      .then(function(product) {

        var productPrice = (product.price / 100).toFixed(2);
        var currencyCode = window.Shopify?.currency?.active || 'USD';

        var ecommercePayload = {
          currency: currencyCode,
          value: parseFloat(productPrice),
          items: [{
            item_id: product.id.toString(),
            item_name: product.title,
            item_brand: product.vendor,
            item_category: product.type,
            price: parseFloat(productPrice),
            quantity: 1
          }]
        };


        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'add_to_wishlist',
          ecommerce: ecommercePayload
        });

      })
      .catch(function(err) {
        console.error('❌ Wishlist Analytics Error:', err);
      });
  }, true); 
})();
